// OrgMultiSelectDropdown.js
// Multi-select org tree with parent-child checkbox rules.
// Checking a parent selects self + all descendants.
// Unchecking a parent deselects self + all descendants.
// Toggling a child only affects that child; parent shows indeterminate when partially selected.
//
// variant="dark"  → header/dark-background style (original)
// variant="light" → panel/light-background style (default)
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";

// Build a flat list with depth and parentId from the org_hierarchy tree
const buildFlatTree = (nodes, parentId = null, depth = 0) => {
  const result = [];
  for (const node of (nodes || [])) {
    const id   = node.organization_id;
    const name = node.organization_name || node.name || id;
    result.push({ id, name, parentId, depth, hasChildren: !!(node.children?.length) });
    if (node.children?.length) {
      result.push(...buildFlatTree(node.children, id, depth + 1));
    }
  }
  return result;
};

const getSubtreeIds = (id, flatTree) => {
  const result = [id];
  for (const node of flatTree) {
    if (node.parentId === id) result.push(...getSubtreeIds(node.id, flatTree));
  }
  return result;
};

const getCheckState = (id, selectedSet, flatTree) => {
  const subtree = getSubtreeIds(id, flatTree);
  const count   = subtree.filter(sid => selectedSet.has(sid)).length;
  if (count === subtree.length) return "checked";
  if (count > 0)               return "indeterminate";
  return "unchecked";
};

// ── Theme tokens ──────────────────────────────────────────────────────────────

const THEMES = {
  dark: {
    btn: {
      background:  "rgba(255,255,255,0.08)",
      border:      "1px solid rgba(255,255,255,0.2)",
      color:       "#ffffff",
      borderRadius: 6,
    },
    btnHover: {
      background: "rgba(255,255,255,0.15)",
    },
    dropdown: {
      background: "#2a2a4a",
      border:     "1px solid rgba(255,255,255,0.15)",
      color:      "#ffffff",
    },
    nodeHover: { background: "rgba(255,255,255,0.06)" },
    childBorder: "1px solid rgba(255,255,255,0.1)",
    countColor: "rgba(255,255,255,0.6)",
  },
  light: {
    btn: {
      background:  "#ffffff",
      border:      "1px solid #ced4da",
      color:       "#212529",
      borderRadius: 6,
    },
    btnHover: {
      background: "#f8f9fa",
    },
    dropdown: {
      background: "#ffffff",
      border:     "1px solid #ced4da",
      color:      "#212529",
    },
    nodeHover: { background: "#f0f4ff" },
    childBorder: "1px solid #dee2e6",
    countColor: "#6c757d",
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

const SPINNER_STYLE = `@keyframes _org-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`;

const OrgMultiSelectDropdown = ({ orgHierarchy, selectedIds, onChange, variant = "light", loading = false }) => {
  const [open,       setOpen]       = useState(false);
  const [pending,    setPending]    = useState(() => new Set(selectedIds || []));
  const [dropPos,    setDropPos]    = useState(null);
  const [btnHovered, setBtnHovered] = useState(false);
  const [search,     setSearch]     = useState("");

  const btnRef    = useRef(null);
  const dropRef   = useRef(null);
  const searchRef = useRef(null);

  const theme   = THEMES[variant] || THEMES.light;

  const flatTree = useMemo(() => buildFlatTree(orgHierarchy), [orgHierarchy]);
  const roots    = useMemo(() => flatTree.filter(n => !n.parentId), [flatTree]);

  // Sync pending when dropdown opens
  useEffect(() => {
    if (open) setPending(new Set(selectedIds || []));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate fixed position from button's bounding rect, flipping upward when near bottom
  const calculatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect         = btnRef.current.getBoundingClientRect();
    const dropH        = 320; // matches maxHeight in the dropdown style
    const spaceBelow   = window.innerHeight - rect.bottom - 8;
    const spaceAbove   = rect.top - 8;
    const openUpward   = spaceBelow < dropH && spaceAbove > spaceBelow;
    const top          = openUpward ? rect.top - Math.min(dropH, spaceAbove) - 4 : rect.bottom + 4;
    const left         = Math.min(rect.left, window.innerWidth - Math.max(rect.width, 260) - 8);
    setDropPos({ top, left, width: Math.max(rect.width, 260) });
  }, []);

  const handleOpen = useCallback(() => {
    if (!open) calculatePosition();
    setOpen(o => !o);
  }, [open, calculatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const clickedBtn  = btnRef.current  && btnRef.current.contains(e.target);
      const clickedDrop = dropRef.current && dropRef.current.contains(e.target);
      if (!clickedBtn && !clickedDrop) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => calculatePosition();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, calculatePosition]);

  const toggle = useCallback((id) => {
    const subtree   = getSubtreeIds(id, flatTree);
    const allPicked = subtree.every(sid => pending.has(sid));
    const next      = new Set(pending);
    if (allPicked) subtree.forEach(sid => next.delete(sid));
    else           subtree.forEach(sid => next.add(sid));
    if (!next.size) next.add(id);
    setPending(next);
  }, [flatTree, pending]);

  const filteredNodes = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return null;
    return flatTree.filter(n => n.name.toLowerCase().includes(term));
  }, [flatTree, search]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => searchRef.current?.focus(), 40);
      return () => clearTimeout(timer);
    }
    setSearch("");
  }, [open]);

  const apply = () => {
    const ids = Array.from(pending);
    const prevSorted = [...(selectedIds || [])].sort();
    const nextSorted = [...ids].sort();
    const unchanged = prevSorted.length === nextSorted.length &&
      prevSorted.every((id, i) => id === nextSorted[i]);
    if (ids.length && !unchanged) onChange(ids);
    setSearch("");
    setOpen(false);
  };

  const cancel = () => {
    setPending(new Set(selectedIds || []));
    setSearch("");
    setOpen(false);
  };

  const buttonLabel = useMemo(() => {
    const ids = selectedIds || [];
    if (ids.length === 0) return "Select org…";
    if (flatTree.length > 0 && ids.length >= flatTree.length) return `All Organizations (${flatTree.length})`;
    if (ids.length === 1) {
      const found = flatTree.find(n => n.id === ids[0]);
      return found?.name || ids[0];
    }
    return `${ids.length} orgs selected`;
  }, [selectedIds, flatTree]);

  const renderNode = useCallback((node) => {
    const state    = getCheckState(node.id, pending, flatTree);
    const children = flatTree.filter(n => n.parentId === node.id);

    return (
      <div key={node.id} style={{ marginBottom: 1 }}>
        <label style={{
          display:     "flex",
          alignItems:  "center",
          gap:          6,
          padding:     "4px 8px",
          borderRadius: 4,
          cursor:      "pointer",
          fontSize:    "0.84rem",
          color:       theme.dropdown.color,
          userSelect:  "none",
        }}
          onMouseEnter={e => Object.assign(e.currentTarget.style, theme.nodeHover)}
          onMouseLeave={e => { e.currentTarget.style.background = ""; }}
        >
          <input
            type="checkbox"
            style={{ cursor: "pointer", flexShrink: 0, accentColor: "#0d6efd" }}
            checked={state === "checked"}
            ref={el => { if (el) el.indeterminate = state === "indeterminate"; }}
            onChange={() => toggle(node.id)}
          />
          {node.hasChildren
            ? <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</strong>
            : <span   style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
          }
        </label>
        {children.length > 0 && (
          <div style={{ marginLeft: 12, paddingLeft: 6, borderLeft: theme.childBorder }}>
            {children.map(renderNode)}
          </div>
        )}
      </div>
    );
  }, [flatTree, pending, toggle, theme]);

  // Dropdown rendered via portal so it escapes any overflow:hidden/auto ancestor
  const dropdownEl = open && dropPos && ReactDOM.createPortal(
    <div
      ref={dropRef}
      style={{
        position:    "fixed",
        top:         dropPos.top,
        left:        dropPos.left,
        minWidth:    dropPos.width,
        maxHeight:   320,
        zIndex:      9999,
        display:     "flex",
        flexDirection: "column",
        overflow:    "hidden",
        borderRadius: 6,
        boxShadow:   "0 4px 16px rgba(0,0,0,0.18)",
        ...theme.dropdown,
      }}
    >
      {/* Select All / Clear row */}
      {flatTree.length > 0 && (
        <div style={{
          display:      "flex",
          gap:           6,
          padding:      "5px 10px",
          borderBottom: `1px solid ${variant === "dark" ? "rgba(255,255,255,0.15)" : "#dee2e6"}`,
          flexShrink:   0,
        }}>
          <button
            type="button"
            onClick={() => setPending(new Set(flatTree.map(n => n.id)))}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "0.75rem", color: "#0d6efd", textDecoration: "underline" }}
          >
            Select all
          </button>
          <span style={{ color: theme.countColor, fontSize: "0.75rem" }}>·</span>
          <button
            type="button"
            onClick={() => setPending(new Set())}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "0.75rem", color: "#0d6efd", textDecoration: "underline" }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Search */}
      {flatTree.length > 0 && (
        <div style={{
          padding:      "5px 8px",
          borderBottom: `1px solid ${variant === "dark" ? "rgba(255,255,255,0.15)" : "#dee2e6"}`,
          flexShrink:   0,
        }}>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width:        "100%",
              padding:      "3px 8px",
              border:       `1px solid ${variant === "dark" ? "rgba(255,255,255,0.25)" : "#ced4da"}`,
              borderRadius:  4,
              fontSize:     "0.82rem",
              background:   variant === "dark" ? "rgba(255,255,255,0.1)" : "#fff",
              color:        variant === "dark" ? "#fff" : "#212529",
              outline:      "none",
            }}
          />
        </div>
      )}

      {/* Tree / filtered list */}
      <div style={{ overflowY: "auto", flex: 1, padding: "4px 6px" }}>
        {filteredNodes !== null
          ? filteredNodes.length === 0
            ? <div style={{ padding: "8px 12px", fontSize: "0.82rem", color: theme.countColor }}>No results</div>
            : filteredNodes.map(node => (
                <label key={node.id} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "4px 8px", borderRadius: 4, cursor: "pointer",
                  fontSize: "0.84rem", color: theme.dropdown.color, userSelect: "none",
                }}
                  onMouseEnter={e => Object.assign(e.currentTarget.style, theme.nodeHover)}
                  onMouseLeave={e => { e.currentTarget.style.background = ""; }}
                >
                  <input
                    type="checkbox"
                    style={{ cursor: "pointer", flexShrink: 0, accentColor: "#0d6efd" }}
                    checked={pending.has(node.id)}
                    onChange={() => toggle(node.id)}
                  />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {node.name}
                  </span>
                </label>
              ))
          : roots.length === 0
            ? <div style={{ padding: "8px 12px", fontSize: "0.82rem", color: theme.countColor }}>No organizations</div>
            : roots.map(renderNode)
        }
      </div>

      {/* Footer */}
      <div style={{
        borderTop:       `1px solid ${variant === "dark" ? "rgba(255,255,255,0.15)" : "#dee2e6"}`,
        padding:         "6px 8px",
        display:         "flex",
        justifyContent:  "space-between",
        alignItems:      "center",
        gap:             6,
        flexShrink:      0,
        background:      theme.dropdown.background,
      }}>
        <span style={{ fontSize: "0.75rem", color: theme.countColor }}>{pending.size} selected</span>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancel}>Cancel</button>
          <button type="button" className="btn btn-sm btn-primary" onClick={apply} disabled={!pending.size}>Apply</button>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <style>{SPINNER_STYLE}</style>
      <button
        ref={btnRef}
        type="button"
        onClick={loading ? undefined : handleOpen}
        onMouseEnter={() => !loading && setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
        title={loading ? "Applying org filter…" : "Select organizations"}
        disabled={loading}
        style={{
          display:        "flex",
          alignItems:     "center",
          gap:             6,
          padding:        "5px 10px",
          cursor:         loading ? "not-allowed" : "pointer",
          fontSize:       "0.85rem",
          whiteSpace:     "nowrap",
          maxWidth:       220,
          width:          "100%",
          transition:     "background 0.15s",
          opacity:        loading ? 0.75 : 1,
          ...(btnHovered && !loading ? { ...theme.btn, ...theme.btnHover } : theme.btn),
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>{buttonLabel}</span>
        {loading
          ? <span style={{
              flexShrink: 0, display: "inline-block",
              width: 10, height: 10,
              border: "2px solid currentColor", borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "_org-spin 0.7s linear infinite",
            }} />
          : <span style={{ flexShrink: 0, fontSize: "0.65em", opacity: 0.7 }}>{open ? "▲" : "▼"}</span>
        }
      </button>

      {dropdownEl}
    </div>
  );
};

export default OrgMultiSelectDropdown;
